import { createI18n } from 'vue-i18n';
import fr from './locales/fr.json';
import en from './locales/en.json';

/** The locales the application ships, and which the profile dialog offers. */
export const SUPPORTED_LOCALES = ['fr', 'en'] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export function isSupportedLocale(value: string | null | undefined): value is SupportedLocale {
  return value != null && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * French and English, with French as the fallback.
 *
 * No user-facing string is written in a component, which is what made adding the second
 * locale a file rather than a pass over every template.
 *
 * The locale a person chose is stored on their account and applied at startup — see
 * `applyStoredLocale`. Until `/users/me` has answered, the interface is in French; switching
 * afterwards is a word-for-word swap, not a reload.
 */
const i18n = createI18n({
  legacy: false,
  locale: 'fr',
  fallbackLocale: 'fr',
  messages: { fr, en },
  pluralRules: {
    /**
     * French agrees the singular with zero: « 0 projet », « 1 projet », « 2 projets ».
     * vue-i18n's default rule puts zero in the plural form, which is the English one — so
     * English is left with the default.
     */
    fr: (choice: number) => (choice <= 1 ? 0 : 1)
  }
});

/** Applies the locale stored on the account, if it is one we ship. */
export function applyLocale(locale: string | null | undefined) {
  if (isSupportedLocale(locale)) i18n.global.locale.value = locale;
}

export default i18n;
